/**
 * Menyusun riwayat absensi yang bisa dibaca manusia dari dua daftar mentah milik server.
 *
 * Aturan yang menentukan seluruh modul ini: **UI tidak pernah menghitung ulang status.** Server
 * sudah memutuskannya memakai `policy_snapshot` yang berlaku saat event terjadi (ADR-0034 B2).
 * Bila UI menyimpulkan sendiri "hadir" atau "terlambat" dari jam check-in, perubahan kebijakan
 * hari ini akan mengubah tampilan absensi bulan lalu — persis yang dilarang.
 *
 * Yang dikerjakan di sini hanya pengelompokan dan pengurutan.
 */

export interface HistoryDay {
  readonly workDate: string;
  readonly status: string | null;
  readonly firstCheckInAt: string | null;
  readonly lastCheckOutAt: string | null;
  readonly lateMinutes: number;
  readonly workedMinutes: number;
}

export interface HistoryEvent {
  readonly id: string;
  readonly eventType: 'CHECK_IN' | 'CHECK_OUT';
  readonly workDate: string;
  readonly occurredAtServer: string;
  readonly locationVerification: string;
}

/** Satu hari beserta event mentahnya — bukan ringkasan saja (acceptance #463). */
export interface HistoryEntry<D extends HistoryDay, E extends HistoryEvent> {
  readonly workDate: string;
  /** `null` bila ada event tetapi projection belum terbentuk untuk hari itu. */
  readonly day: D | null;
  readonly events: readonly E[];
  /** Benar bila ada satu saja event yang lokasinya tidak terverifikasi. */
  readonly hasUnverifiedLocation: boolean;
}

/** `YYYY-MM` dari `YYYY-MM-DD`, tanpa melewati `Date` sama sekali. */
export function monthOf(workDate: string): string {
  return workDate.slice(0, 7);
}

/**
 * Rentang tanggal penuh sebuah bulan, sebagai string.
 *
 * Sengaja TIDAK memakai `new Date(...)`: konstruktor Date menafsirkan `YYYY-MM-DD` sebagai UTC
 * lalu `getDate()` membacanya kembali di timezone browser, sehingga tanggal 1 bisa bergeser ke
 * tanggal terakhir bulan sebelumnya bagi siapa pun di sebelah barat UTC. Jumlah hari per bulan
 * adalah aritmetika kalender, bukan urusan zona waktu.
 */
export function monthRange(month: string): { readonly from: string; readonly to: string } {
  const [yearText, monthText] = month.split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText);
  const lastDay = daysInMonth(year, monthIndex);
  return {
    from: `${month}-01`,
    to: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function daysInMonth(year: number, monthOneBased: number): number {
  if (monthOneBased === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_PER_MONTH[monthOneBased - 1] ?? 30;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Bulan sebelum/sesudah, tetap sebagai string `YYYY-MM`. */
export function shiftMonth(month: string, delta: number): string {
  const [yearText, monthText] = month.split('-');
  const total = Number(yearText) * 12 + (Number(monthText) - 1) + delta;
  const year = Math.floor(total / 12);
  const monthIndex = total - year * 12 + 1;
  return `${String(year).padStart(4, '0')}-${String(monthIndex).padStart(2, '0')}`;
}

/**
 * Menggabungkan hari dan event menjadi satu daftar, terbaru lebih dulu.
 *
 * Event dikelompokkan menurut `workDate` MILIK EVENT, bukan menurut jam kejadiannya. Itulah
 * yang membuat shift lintas tengah malam benar: check-out pukul 01:00 membawa `workDate` hari
 * sebelumnya karena ia mewarisi hari kerja sesi yang dibuka (lihat `performCheckOut`). Mengelompokkan
 * ulang berdasarkan `occurredAtServer` akan memecah satu shift menjadi dua hari yang keduanya
 * terlihat tidak lengkap.
 */
export function buildHistory<D extends HistoryDay, E extends HistoryEvent>(
  days: readonly D[],
  events: readonly E[],
): readonly HistoryEntry<D, E>[] {
  const byDate = new Map<string, { day: D | null; events: E[] }>();

  for (const day of days) {
    byDate.set(day.workDate, { day, events: [] });
  }
  for (const event of events) {
    const bucket = byDate.get(event.workDate);
    if (bucket) {
      bucket.events.push(event);
    } else {
      byDate.set(event.workDate, { day: null, events: [event] });
    }
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([workDate, bucket]) => ({
      workDate,
      day: bucket.day,
      events: [...bucket.events].sort((a, b) =>
        a.occurredAtServer < b.occurredAtServer
          ? -1
          : a.occurredAtServer > b.occurredAtServer
            ? 1
            : 0,
      ),
      hasUnverifiedLocation: bucket.events.some((e) => e.locationVerification !== 'VERIFIED'),
    }));
}

/**
 * Benar bila sesi hari itu melewati tengah malam — check-out terjadi pada tanggal kalender
 * yang berbeda dari `workDate`-nya.
 *
 * Ditampilkan sebagai keterangan, bukan sebagai peringatan: shift malam itu normal, dan
 * menandainya sebagai anomali akan membuat kurir malam melihat "peringatan" setiap hari.
 * Yang perlu dicegah hanyalah pembaca menyangka jamnya salah ketik.
 */
export function crossesMidnight<E extends HistoryEvent>(
  entry: HistoryEntry<HistoryDay, E>,
  timeZone: string,
): boolean {
  const checkOut = [...entry.events].reverse().find((e) => e.eventType === 'CHECK_OUT');
  if (!checkOut) {
    return false;
  }
  return calendarDateInZone(checkOut.occurredAtServer, timeZone) !== entry.workDate;
}

/**
 * Tanggal kalender sebuah instant pada zona tertentu, sebagai `YYYY-MM-DD`.
 *
 * `en-CA` dipakai karena format bawaannya memang `YYYY-MM-DD`; ini pilihan teknis untuk
 * mendapatkan urutan tahun-bulan-hari, bukan pilihan bahasa tampilan.
 */
export function calendarDateInZone(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}
